import { notifications } from '@mantine/notifications';

import type { QueryClient } from '@tanstack/react-query';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  createVaultProperties,
  deleteVaultProperty,
  getVaultProperties,
  getVaultProperty,
  updateVaultProperty,
} from '../../../../../shared/api/magellan/vaultProperties';
import type {
  CreateVaultPropertyRequestDTO,
  UpdateVaultPropertyRequestDTO,
} from '../../../../../shared/api/magellan/vaultProperties/dto';
import { displayErrorNotification } from '../../../../../shared/components/notifications/errorNotification';
import type { VaultProperty } from '../../../../../shared/entities/VaultProperty';
import { getBidResponsesQueryKey } from './bidResponse.hook';

export const getVaultPropertiesQueryKey = [getVaultProperties.name];

export function useGetVaultPropertiesQuery() {
  return useSuspenseQuery({
    queryKey: getVaultPropertiesQueryKey,
    queryFn: getVaultProperties,
  });
}

export function useGetVaultPropertyQuery(id?: number) {
  const queryKey = [getVaultProperty.name, id];

  return useSuspenseQuery({
    queryKey,
    queryFn: () => {
      if (!id) {
        return null;
      }

      return getVaultProperty(id);
    },
  });
}

export function useCreateVaultPropertyMutation() {
  const { t } = useTranslation(['notification', 'bidResponse']);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateVaultPropertyRequestDTO) =>
      createVaultProperties(payload),
    onMutate: async newProperty => {
      await queryClient.cancelQueries({
        queryKey: getVaultPropertiesQueryKey,
      });

      queryClient.setQueryData(
        getVaultPropertiesQueryKey,
        (old: VaultProperty[]) => {
          return [...old, newProperty];
        },
      );

      const previousProperties = queryClient.getQueryData<VaultProperty[]>(
        getVaultPropertiesQueryKey,
      );
      return { previousProperties };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(
        getVaultPropertiesQueryKey,
        context?.previousProperties,
      );
      displayErrorNotification(
        t('notification:errors.somethingWentWrong.title'),
        t('notification:errors.somethingWentWrong.message'),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getVaultPropertiesQueryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getBidResponsesQueryKey,
      });
    },
    onSuccess: () => {
      notifications.show({
        message: t(
          'bidResponse:vault.sidepanels.createProperty.successMessage',
        ),
        color: 'primary.6',
        autoClose: 2000,
        withCloseButton: false,
      });
    },
  });
}

type UpdateVaultPropertyMutationProps = {
  payload: UpdateVaultPropertyRequestDTO;
  vaultPropertyId: number;
  withTagsInference?: boolean;
};

export function useUpdateVaultPropertyMutation() {
  const { t } = useTranslation(['notification', 'bidResponse']);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      vaultPropertyId,
      withTagsInference,
    }: UpdateVaultPropertyMutationProps) => {
      return updateVaultProperty(vaultPropertyId, payload, withTagsInference);
    },
    onMutate: async ({ payload, vaultPropertyId }) => {
      const newProperty = {
        ...payload,
        id: vaultPropertyId,
      };

      const { previousProperties } = await optimisticUpdateVaultProperties(
        queryClient,
        newProperty,
      );
      const { previousProperty } = await optimisticUpdateVaultProperty(
        queryClient,
        newProperty,
      );

      return { previousProperties, previousProperty };
    },
    onSuccess: () => {
      notifications.show({
        message: t(
          'bidResponse:vault.sidepanels.updateProperty.successMessage',
        ),
        color: 'primary.6',
        autoClose: 2000,
        withCloseButton: false,
      });
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(
        getVaultPropertiesQueryKey,
        context?.previousProperties,
      );
      queryClient.setQueryData(
        [getVaultProperty.name, variables.vaultPropertyId],
        context?.previousProperty,
      );
      displayErrorNotification(
        t('notification:errors.somethingWentWrong.title'),
        t('notification:errors.somethingWentWrong.message'),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getVaultPropertiesQueryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getBidResponsesQueryKey,
      });
    },
  });
}

async function optimisticUpdateVaultProperties(
  queryClient: QueryClient,
  newPropertyData: UpdateVaultPropertyRequestDTO,
) {
  await queryClient.cancelQueries({
    queryKey: getVaultPropertiesQueryKey,
  });

  const previousProperties = queryClient.getQueryData<VaultProperty[]>(
    getVaultPropertiesQueryKey,
  );

  const updatedProperty = previousProperties?.find(
    property => property.id === newPropertyData.id,
  );

  if (!updatedProperty) {
    throw new Error('Property not found');
  }

  const updatedProperties = previousProperties?.map(property =>
    property.id === newPropertyData.id
      ? { ...property, ...newPropertyData }
      : property,
  );

  queryClient.setQueryData(getVaultPropertiesQueryKey, () => updatedProperties);

  return { previousProperties };
}

async function optimisticUpdateVaultProperty(
  queryClient: QueryClient,
  newPropertyData: UpdateVaultPropertyRequestDTO,
) {
  const queryKey = [getVaultProperty.name, newPropertyData.id];

  await queryClient.cancelQueries({
    queryKey,
  });

  const previousProperty = queryClient.getQueryData<VaultProperty>(queryKey);
  let options = previousProperty?.options;

  if (newPropertyData.options?.update) {
    options = options?.map(option =>
      newPropertyData.options?.update?.find(o => o.id === option.id)
        ? {
            ...option,
            ...newPropertyData.options.update.find(o => o.id === option.id),
          }
        : option,
    );
  }
  if (newPropertyData.options?.create) {
    options = [
      ...(options || []),
      ...(newPropertyData.options.create.map((option, index) => ({
        ...option,
        id: index,
      })) || []),
    ];
  }
  if (newPropertyData.options?.delete) {
    options = options?.filter(
      option => !newPropertyData.options?.delete?.includes(option.id),
    );
  }
  const updatedProperty = {
    ...previousProperty,
    ...newPropertyData,
    options,
  };

  queryClient.setQueryData(queryKey, () => updatedProperty);

  return { previousProperty };
}

export function useDeleteVaultPropertyMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vaultPropertyId: number) =>
      deleteVaultProperty(vaultPropertyId),
    onMutate: async vaultPropertyId => {
      await queryClient.cancelQueries({
        queryKey: getVaultPropertiesQueryKey,
      });

      const previousProperties = queryClient.getQueryData<VaultProperty[]>(
        getVaultPropertiesQueryKey,
      );

      queryClient.setQueryData(getVaultPropertiesQueryKey, () =>
        previousProperties?.filter(property => property.id !== vaultPropertyId),
      );

      return { previousProperties };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(
        getVaultPropertiesQueryKey,
        context?.previousProperties,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getVaultPropertiesQueryKey,
      });
    },
  });
}
